/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - Класс Date</h2>
    <p className={styles.description}>
      Работа с промежутками времени - это одна из наиболее популярных задач: часы, календари и прочее. JavaScript
      предоставляет класс <code className={styles.code}>Date</code>, который абстрагирует большую часть работы с датами
      напрямую. Это позволяет нам представлять моменты во времени и манипулировать ими всего несколькими строками кода.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Создание даты</h3>'
      <p className={styles.description}>
        Создание даты очень просто. Просто создайте новый экземпляр встроенного класса{' '}
        <code className={styles.code}>Date</code>.{' '}
      </p>
      <CopyBlock
        text={`const date = new Date();
console.log(date); // Mon Mar 19 2018 16:51:32 GMT+0200 (FLE Standard Time)`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Поскольку не были переданы аргументы, будет создана текущая дата и время. Тем не менее, можно дополнительно
        передать строку или число в <code className={styles.code}>Date</code> и результатом будет объект, описывающий
        указанную дату и время.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.1.1 - Datestring</h4>
        <p className={styles.description}>
          Если передать строку, она будет обработана методом <code className={styles.code}>Date.parse()</code>, чтобы
          вернуть объект <code className={styles.code}>Date</code>.{' '}
        </p>
        <p className={styles.description}>
          Допустим необходимо создать событие на дату <code className={styles.code}>4 марта 2017 года</code>.{' '}
        </p>
        <CopyBlock
          text={`const date = new Date('March 4, 2017');
console.log(date); // Sat Mar 04 2017 00:00:00 GMT+0200 (FLE Standard Time)`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.1.2 - 7 аргументов</h4>
        <p className={styles.description}>
          Другой способ создания новых объектов <code className={styles.code}>Date</code> — это передать 7 чисел. Эти
          числа описывают год, месяц, день, час, минуту, секунду и миллисекунду.{' '}
        </p>
        <CopyBlock
          text={`new Date(year, month, date, hours, minutes, seconds, ms)`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>Последние 4 аргумента необязательны. </p>
        <CopyBlock
          text={`const date = new Date(2000, 5, 8, 12, 0, 0, 0);

// Индексы месяцев начинаются с 0, поэтому 5-й месяц это июль.
console.log(date); // Thu Jun 08 2000 12:00:00 GMT+0300 (FLE Daylight Time)`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Unix время</h3>
      <p className={styles.description}>
        В области информатики существует концепция эпохи Unix. Этот момент времени установлен на полночь 1 января 1970
        года в часовом поясе UTC.{' '}
      </p>
      <p className={styles.description}>
        С учетом этой системы отсчета, если вы передаете число объекту <code className={styles.code}>Date</code>, это
        число представляет собой количество миллисекунд, прошедших с тех пор.{' '}
      </p>
      <CopyBlock
        text={`const date = new Date(1000000);
console.log(date); // Thu Jan 01 1970 02:16:40 GMT+0200 (FLE Standard Time)`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Чтобы увидеть число миллисекунд от этой даты до текущего момента, используется статический метод{' '}
        <code className={styles.code}>Date.now()</code>.{' '}
      </p>
      <CopyBlock
        text={`const date = Date.now();
console.log(date); // 1521471595501`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Удобство этого формата заключается в том, что можно представлять точные моменты времени в виде одного числа и не
        беспокоиться о датах, строках и часовых поясах, так как можно получить всю необходимую информацию когда
        необходимо.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.3 - Методы Date</h3>
      <p className={styles.description}>
        Экземпляр класса <code className={styles.code}>Date</code> имеет множество методов как для получения значений
        даты и времени, так и для установки значений в объект. Методы возвращают или присваивают год, месяц, день месяца
        или недели, час, минуту, секунду и миллисекунду для каждого экземпляра. Эти данные могут быть представлены в
        виде строки с учетом местного календаря или языка.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.3.1 - Геттеры</h4>
        <p className={styles.description}>
          Вот все методы получения информации из объекта <code className={styles.code}>Date</code>:
        </p>
        <CopyBlock
          text={`const date = new Date();
console.log('Date: ' + date);

// возвращает день месяца от 1 до 31
console.log('getDate(): ' + date.getDate());

// возвращает день недели от 0 до 6
console.log('getDay(): ' + date.getDay());

// возвращает месяц от 0 до 11
console.log('getMonth(): ' + date.getMonth());

// возвращает год из 4 цифр
console.log('getFullYear(): ' + date.getFullYear());

// возвращает час
console.log('getHours(): ' + date.getHours());

// возвращает минуты
console.log('getMinutes(): ' + date.getMinutes());

// возвращает секунды
console.log('getSeconds(): ' + date.getSeconds());

// возвращает миллисекунды
console.log('getMilliseconds(): ' + date.getMilliseconds());

// возвращает кол-во миллисекунд прошедших со старта эпохи Unix
console.log('getTime(): ' + date.getTime());`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>Давайте поэкспериментируем: </p>
        <CopyBlock
          text={`const date = new Date();

// проверим количество мс с с начала эпохи Unix
console.log(\`Время в мс с начала эпохи Unix: $ {date.getTime()}\`);
// "Время в мс с начала эпохи Unix: ....  значения будут меняться :-)
// "Время в мс с начала эпохи Unix: 1504721892483"

let result = 'Date: ';
result += date.getFullYear() + '/'; // год
result += date.getMonth() + '/'; // месяц, начиная с 0
result += date.getDate() + '. Day of week: '; // день недели, начиная с 1
result += date.getDay() + '.'; // день , начиная с 0

result += ' Time: ' + date.getHours() + ':'; // часы
result += date.getMinutes() + ':'; // минуты
result += date.getSeconds() + ':'; // секунды
result += date.getMilliseconds() + ''; // милисекунды

// строка должна показать текущее время и дату
console.log(result); // "Date: 2017/8/6. Day of week: 3. Time: 21:18:12:483"
// ...  значения будут меняться :-)`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.3.2 - Сеттеры</h4>
        <p className={styles.description}>
          Все, что можно прочитать можно записать, методы для записи начинаются с{' '}
          <code className={styles.code}>set*</code>.
        </p>
        <CopyBlock
          text={`// Для установки даты существует 2 способа. Установим 1500000000000мс
// с начала эры Unix с помощью конструктора
const date = new Date(2017, 6, 14, 5, 40, 0, 0);

// Проверим
console.log(\`2017, 6, 14, 5, 40 соответствует $ {date.getTime()}\`);
// "2017, 6, 14, 5, 40 соответствует 1500000000000"

/* С помощью методов set */
// Создадим новый объект с текущей датой
const secondDate = new Date();

// Изменим год и в нем же месяц и день месяца
secondDate.setFullYear(2017, 6, 14);
// Изменим час и в нем же минуты, секунды и милисекунды
secondDate.setHours(5, 40, 0, 0);

// Проверим
console.log(\`2017, 6, 14, 5, 40 соответствует $ {secondDate.getTime()}\`);
// "2017, 6, 14, 5, 40 соотвествует 1500000000000"`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.4 - Форматирование и вывод дат</h3>
      <p className={styles.description}>
        Во всех браузерах, кроме <code className={styles.code}>IE10-</code>, поддерживается новый стандарт, который
        добавляет специальные методы для форматирования дат.
      </p>
      <p className={styles.description}>
        Это делается вызовом <code className={styles.code}>date.toLocaleString(locale, opts)</code>, в котором можно
        задать много настроек. Он позволяет указать, какие параметры даты нужно вывести и ряд настроек вывода, после
        чего интерпретатор сам сформирует строку.
      </p>
      <CopyBlock
        text={`let date = new Date();

// формат вывода
const options = {
  weekday: 'long',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

// Украина
const localeUk = date.toLocaleString('Uk-uk', options);
console.log(localeUk); // понеділок, 19 бер. 2018 р., 17:42

// USA
const localeUs = date.toLocaleString('en-US', options);
console.log(localeUs); // Monday, Mar 19, 2018, 5:43 PM`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <section className={styles.subsection}>
        <h4 className={styles.h4}>2.4.1 - Методы вывода без локализации</h4>
        <p className={styles.description}>
          <code className={styles.code}>toString()</code>, <code className={styles.code}>toDateString()</code>,{' '}
          <code className={styles.code}>toTimeString()</code> возвращают стандартное строчное представление, не заданное
          жёстко в стандарте, а зависящее от браузера. Единственное требование к нему — читаемость человеком. Метод{' '}
          <code className={styles.code}>toString()</code> возвращает дату целиком,{' '}
          <code className={styles.code}>toDateString()</code> и <code className={styles.code}>toTimeString()</code> —
          только дату и время соответственно.
        </p>
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.5 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://canonium.com/articles/javascript-date-and-time"
            target="_blank"
            rel="noopener noreferrer"
          >
            Дата и время в JavaScript
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://flaviocopes.com/javascript-dates/"
            target="_blank"
            rel="noopener noreferrer"
          >
            The definitive guide to JavaScript dates
          </a>
        </li>
        <li className={styles.list__item}>
          <a className={styles.link} href="https://momentjs.com/" target="_blank" rel="noopener noreferrer">
            Moment.js — библиотека работы с временем
          </a>
        </li>
        <li className={styles.list__item}>
          <a className={styles.link} href="https://flaviocopes.com/momentjs/" target="_blank" rel="noopener noreferrer">
            A moment.js tutorial
          </a>
        </li>
        <li className={styles.list__item}>
          <a className={styles.link} href="https://github.com/iamkun/dayjs" target="_blank" rel="noopener noreferrer">
            dayjs - библиотека работы с временем
          </a>
        </li>
        <li className={styles.list__item}>
          <a className={styles.link} href="https://date-fns.org/" target="_blank" rel="noopener noreferrer">
            date-fns — библиотека работы с временем
          </a>
        </li>
      </ul>
    </section>
  </section>
))
